import {
  DatabaseAuto,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IcommerceUser, IdataArrayResponse,
  IdeleteOneResponse, IfilterProps,
  Iitem,
  IitemComplaint,
  TitemComplaintRequest
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../../commerce-client';

/**
 * Type alias for item complaint properties with specific generic types
 * @template T - Type for order (default: IecommerceOrder<number, IcommerceUser>)
 * @template C - Type for company (default: Icompany)
 * @template U - Type for user (default: IcommerceUser)
 */
type TitemComplaintProps = IitemComplaint<Iitem, IcommerceUser>;

/**
 * Represents an item complaint for a specific order.
 * Extends DatabaseAuto to inherit common database operations.
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link ItemComplaint} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const itemComplaint = await ItemComplaint.getAll();
 *
 * const itemComplaint = await ItemComplaint.filterAll({
 *
 * });
 *
 * const itemComplaint = await ItemComplaint.bulkDelete({
 *
 * });
 *
 * const itemComplaint = await ItemComplaint.getOne('resourceId');
 *
 * const itemComplaint = await ItemComplaint.remove();
 *
 * ```
 */
export class ItemComplaint
  extends DatabaseAuto {
  private static readonly baseUrl = '/item-complaint';
  readonly item: TitemComplaintProps['item'];
  readonly reason: TitemComplaintProps['reason'];
  readonly commerceUser: TitemComplaintProps['commerceUser'];
  readonly status: TitemComplaintProps['status'];
  readonly handler: TitemComplaintProps['handler'];

  /**
   * Constructs an instance of `ItemComplaint`.
   *
   * @param {TitemComplaintProps} data - The itemComplaint data used to initialize the instance.
   * @throws {Error} Throws an error if no data is provided.
   */
  constructor(data: TitemComplaintProps) {
    super(data);

    this.item = data.item;
    this.reason = data.reason;
    this.commerceUser = data.commerceUser;
    this.status = data.status;
    this.handler = data.handler;
  }

  /**
   * Retrieves all itemcomplaints with pagination.
   *
   * @param {number} offset - The starting point for the retrieval.
   * @param {number} limit - The number of records to retrieve.
   * @returns {Promise<{ count: number; dataArr: ItemComplaint[] }>}
   * A promise resolving to an object containing count and data array.
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TitemComplaintProps>>(`${ItemComplaint.baseUrl}?${query}`);
    const itemComplaints = await lastValueFrom(observer$);

    return {
      count: itemComplaints.count,
      dataArr: itemComplaints.data.map((val) => new ItemComplaint(val))
    };
  }

  /**
   * Filters itemcomplaints based on the provided criteria.
   *
   * @param {IfilterProps} filter - The filter criteria.
   * @returns {Promise<{ count: number; dataArr: ItemComplaint[] }>}
   * A promise resolving to an object containing count and data array.
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TitemComplaintProps>>(
        `${ItemComplaint.baseUrl}/filter`,
        filter
      );
    const itemComplaints = await lastValueFrom(observer$);

    return {
      count: itemComplaints.count,
      dataArr: itemComplaints.data.map((val) => new ItemComplaint(val))
    };
  }

  /**
   * Retrieves a single itemcomplaint by resource ID.
   *
   * @param {string} resourceId - The ID of the itemcomplaint to retrieve.
   * @returns {Promise<ItemComplaint>} The retrieved `ItemComplaint` instance.
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TitemComplaintProps>(`${ItemComplaint.baseUrl}/${resourceId}`);
    const itemComplaint = await lastValueFrom(observer$);

    return new ItemComplaint(itemComplaint);
  }

  /**
   * Creates a new item complaint with the provided data.
   *
   * @param {TitemComplaintRequest<true>} payload - The complaint data to create
   * @returns {Promise<ItemComplaint>} The newly created ItemComplaint instance
   * @throws {Error} When the creation fails due to invalid data or API error
   *
   * @example
   * const complaint = await ItemComplaint.add({
   *   orderId: 'order123',
   *   reason: 'Item not as described',
   *   status: 'pending'
   * });
   */
  static async add(payload: TitemComplaintRequest<true>) {
    const observer$ = CommerceClient.ehttp
      .makePost<TitemComplaintProps>(
        `${ItemComplaint.baseUrl}`,
        payload
      );

    return new ItemComplaint(await lastValueFrom(observer$));
  }

  /**
   * Removes multiple item complaints based on the provided criteria.
   *
   * @param {IbulkDelete} payload - The criteria for bulk deletion
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk delete operation
   * @throws {Error} When the deletion fails or payload is invalid
   *
   * @example
   * const result = await ItemComplaint.bulkDelete({
   *   ids: ['complaint1', 'complaint2'],
   *   hardDelete: false
   * });
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(
        `${ItemComplaint.baseUrl}/bulk-delete`,
        payload
      );

    return lastValueFrom(observer$);
  }

  /**
   * Updates the current item complaint with new data.
   *
   * @param {TitemComplaintRequest} payload - The updated complaint data
   * @returns {Promise<ItemComplaint>} The updated ItemComplaint instance
   * @throws {Error} When the update fails or payload is invalid
   *
   * @example
   * const complaint = await ItemComplaint.getOne('complaint123');
   * const updated = await complaint.update({
   *   status: 'resolved',
   *   resolution: 'Refund issued'
   * });
   */
  async update(payload: TitemComplaintRequest) {
    const observer$ = CommerceClient.ehttp
      .makePut<TitemComplaintProps>(
        `${ItemComplaint.baseUrl}/${this._id}`,
        payload
      );

    return new ItemComplaint(await lastValueFrom(observer$));
  }

  /**
   * Removes the current item complaint from the system.
   *
   * @returns {Promise<IdeleteOneResponse | null>} The result of the delete operation
   * @throws {Error} When the deletion fails
   *
   * @example
   * const complaint = await ItemComplaint.getOne('complaint123');
   * const result = await complaint.remove();
   * if (result?.success) {
   *   console.log('Complaint deleted successfully');
   * }
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${ItemComplaint.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
